/**
 * Trademate's widgets, re-skinned to the shell.
 *
 * Autochartist themes each widget per broker with a stylesheet of its own
 * (`826-*-app.css` on broker-resources), and every one of them is in live's old
 * navy palette — #161d26 / #1b2430 / #222c3a / #293647, with 2px #293647
 * outlines round every card. Each widget takes a replacement stylesheet in its
 * `css` parameter, so each skin below imports Autochartist's own sheet first
 * (layout, icons and anything not restated here stay theirs) and then restates
 * just the coloured rules in this app's palette.
 *
 * The values are raw, not tokens — the frames are another origin and can't
 * read our custom properties — each one the raw value of the token named
 * beside it in PALETTE. Outlines go, per the Style Guide: depth comes from
 * the surface colours alone.
 *
 * Delivered as `data:` URLs rather than files under /public: a public page
 * fetching from a private address is blocked outright by Chrome (Private
 * Network Access), so a hosted sheet would only ever work in production —
 * and a data URL needs no hosting, cache-busting or CORS at all.
 *
 * When Autochartist changes a widget's markup, the fix is here: open the
 * widget's URL on its own, inspect the element, restate its rule below.
 */

const PALETTE = {
  page: '#0e0e0e', // --bg-surface: the Trademate card the frame sits in
  raised: '#161616', // --bg-raised: cards, rows, inputs inside the widget
  hover: '#1c1c1c', // the raised hover used by the Get started rows
  line: 'rgba(255, 255, 255, 0.05)', // --hairline
  text: '#f7f8f8', // --text-primary
  soft: '#9a9a9a', // --text-soft
  muted: '#777777', // --text-muted
  blue: '#1a5dfd', // --grad-tabs' first stop, for solid fills
  link: '#4d84ff', // the shell's link hover blue
  grad: 'linear-gradient(105deg, #1a5dfd, #103897)', // --grad-tabs
};

const BROKER = 'https://broker-resources.autochartist.com/css/components';
const { page, raised, hover, line, text, soft, muted, blue, link, grad } = PALETTE;

/**
 * What every widget gets: the shell's font and surfaces, and a quiet
 * scrollbar. The font skips `<i>`, `.fa` and `.glyphicon` — the widgets' Font
 * Awesome and Glyphicon icons, which would otherwise render as tofu.
 */
const BASE = `
@import url('https://fonts.googleapis.com/css2?family=Inter:wght@400;500;600&display=swap');
html, body { background: ${page} !important; color: ${text}; }
body, body :not(i):not(.fa):not(.glyphicon) { font-family: Inter, system-ui, sans-serif !important; }
a { color: ${link}; }
::-webkit-scrollbar { width: 8px; height: 8px; }
::-webkit-scrollbar-track { background: transparent !important; }
::-webkit-scrollbar-thumb { background: #262626 !important; border-radius: 4px; }
`;

/** The shell's primary pill — `Button variant="pill"`. */
const PILL = `background: ${grad} !important; border: 0 !important; border-radius: 5px !important; color: #fff !important; box-shadow: none !important;`;

const SKINS = {
  /** Trading Opportunity — a grid of pattern cards under a Forex/Commodities/Indices bar. */
  to: `
@import url('${BROKER}/826-to-dark-app.css');
${BASE}
#horizontal-layout.container { width: auto !important; max-width: none !important; padding: 0 4px !important; }
.btn-sm, .btn { font-size: 11.5px !important; padding: 5px 10px !important; line-height: 1.2 !important; }
.list-group-item-heading.symbol-row { font-size: 15px !important; }
#horizontal-layout .navbar-default { background: transparent !important; box-shadow: none !important; border: 0 !important; }
#horizontal-layout .navbar-nav > li > a { color: ${muted} !important; font-size: 12px !important; border: 0 !important; border-radius: 7px; }
#horizontal-layout .navbar-nav > li:hover > a { color: ${soft} !important; }
#horizontal-layout .navbar-default .navbar-nav > .active > a,
#horizontal-layout .navbar-default .navbar-nav > .active > a:focus,
#horizontal-layout .navbar-default .navbar-nav > .active > a:hover { background: ${grad} !important; color: #fff !important; border: 0 !important; }
.list-group-item, #horizontal-layout .list-group-item, #horizontal-layout .list-group-item:first-child {
  background: ${raised} !important; border: 0 !important; border-radius: 10px !important; box-shadow: none !important;
}
.well, #horizontal-layout .well, .active .well, .active .new-item { background: transparent !important; border: 0 !important; box-shadow: none !important; color: ${soft} !important; }
.list-group-item-heading, .symbol, .interval { color: ${text} !important; }
small, .small, .time, #horizontal-layout small, #horizontal-layout .small { color: ${muted} !important; }
.btn, .btn-danger, .label-success { ${PILL} }
.panel, .panel-body { background: ${raised} !important; border: 0 !important; }
.pagination > li > a, .pagination > li > span, #horizontal-layout .pagination > li > a, #horizontal-layout .pagination > li > span {
  background: ${raised} !important; border: 0 !important; color: ${soft} !important; box-shadow: none !important;
}
.pagination > .active > a, #horizontal-layout .pagination > .active > a { background: ${blue} !important; color: #fff !important; }
.dropdown-menu { background: ${raised} !important; border: 0 !important; }
.dropdown-menu > li > a { color: ${text} !important; }
.dropdown-menu > li > a:hover { background: ${hover} !important; }
/* The phone layout's menu button — a raised control rather than a blue tile. */
#horizontal-layout .navbar-toggle { background: ${raised} !important; border: 0 !important; border-radius: 7px; }
.navbar-default .navbar-toggle .icon-bar { background-color: ${soft} !important; }
#horizontal-layout .navbar-collapse { border: 0 !important; box-shadow: none !important; }
@media (max-width: 767px) {
  .list-group-item { padding: 10px !important; }
  .col-xs-12 { padding: 0 !important; }
}
`,

  /** Volatility Analysis — striped symbol tables, then a per-symbol detail view. */
  va: `
@import url('${BROKER}/826-va-dark-app.css');
${BASE}
.container { width: auto !important; max-width: none !important; }
.table, .odd, tr.odd, .table-bordered { background: transparent !important; border: 0 !important; }
.even, tr.even { background: ${raised} !important; }
.tbody tr.odd:hover, tr.even:hover, tr.odd:hover { background: ${hover} !important; }
.table > thead > tr > th, .table-bordered th, .table-bordered > thead > tr > th, .table > caption + thead > tr:first-child > th,
.table > colgroup + thead > tr:first-child > th, .table > thead:first-child > tr:first-child > th {
  background: transparent !important; border: 0 !important; border-bottom: 1px solid ${line} !important; color: ${muted} !important; font-weight: 400;
}
.table > tbody > tr > td, .table-bordered > tbody > tr > td, .cell-border-right { border: 0 !important; border-top: 1px solid ${line} !important; }
.well { background: ${raised} !important; border: 0 !important; border-radius: 10px; box-shadow: none !important; color: ${soft} !important; }
.va-header, .h3, h3, h4, h5 { color: ${text} !important; }
.btn, .input-group-addon { ${PILL} }
.form-control { background: ${raised} !important; border: 0 !important; box-shadow: none !important; color: ${text} !important; }
.pagination > li > a, .pagination > li > span, .pagination > .disabled > a { background: ${raised} !important; border: 0 !important; color: ${soft} !important; }
.pagination > .active > a, .pagination > .active > span { background: ${blue} !important; color: #fff !important; }
.panel, .panel-footer { background: transparent !important; border: 0 !important; }
.tree-control .tree-view { background: ${raised} !important; }
.tree-control .tree-view .selected, .tree-control .tree-view .active, .tree-control .tree-view .selected.active {
  background: ${hover} !important; border-color: transparent !important;
}
.tree-control .tree-view .item-container:hover { color: ${link} !important; border-color: transparent !important; }
.terms > a, .glyphicon-next, .glyphicon-previous, span.glyphicon.glyphicon-list { color: ${link} !important; }
a:focus, a:hover { color: ${soft}; }
`,

  /** Economic Calendar — one long event table with an impact filter bar. */
  calendar: `
@import url('${BROKER}/826-calendar-dark-app.css');
${BASE}
.container, .container-fluid { border: 0 !important; width: auto !important; max-width: none !important; }
.table > tbody > tr > td, .table > tbody > tr > th, .table > tfoot > tr > td, .table > thead > tr > td { border-top: 1px solid ${line} !important; }
.table > thead > tr > th, th { border-bottom: 1px solid ${line} !important; color: ${muted} !important; font-weight: 400; }
.table-striped > tbody > tr:nth-child(odd) > td, .table-striped > tbody > tr:nth-child(odd) > th { background: transparent !important; }
.row-header, td.row-header, .panel-default > .panel-heading, .navbar-default { background: ${raised} !important; border: 0 !important; color: ${text} !important; }
.table-hover > tbody > tr:hover > td, .table-hover > tbody > tr:hover > th, .table > tbody > tr.active > td { background: ${hover} !important; }
.btn { ${PILL} }
span.fa.fa-star.checked { color: ${blue} !important; }
.glyphicon { color: ${link} !important; background: transparent !important; }
.panel { background: ${raised} !important; border: 0 !important; box-shadow: none !important; }
.dropdown-menu { background: ${raised} !important; border: 0 !important; }
.dropdown-menu > li > a { color: ${text} !important; }
.dropdown-menu > .active > a, .dropdown-menu > li > a:hover { background: ${blue} !important; color: #fff !important; }
.navbar-default .navbar-nav > .open > a { background: ${blue} !important; }
`,

  /** News Feed — Bootstrap 5: a search row, then a list of article cards. */
  news: `
@import url('${BROKER}/826-news-feeds-dark-app.css');
${BASE}
:root { --bs-body-bg: ${page}; --bs-body-color: ${text}; --bs-border-color: ${line}; --bs-link-color: ${link}; }
.navbar-default { background: transparent !important; border: 0 !important; }
.navbar-default .navbar-nav a { color: ${muted} !important; border-radius: 7px; }
.navbar-default .navbar-nav a.active, .navbar-default .navbar-nav a.active:focus, .navbar-default .navbar-nav a.active:hover {
  background: ${grad} !important; color: #fff !important;
}
div.news-item, .news-item { background: ${raised} !important; border: 0 !important; border-radius: 10px !important; box-shadow: none !important; }
.form-control, .form-select, .dropdown.dropdown-combobox .dropdown-toggle {
  background: ${raised} !important; border: 0 !important; border-radius: 7px !important; color: ${text} !important; box-shadow: none !important;
}
.form-control:focus { outline: 2px solid ${blue}; }
.trending-stocks-btn, .btn-primary { ${PILL} font-size: 12px !important; }
.source-name-text, .read-article-link, .dropdown.dropdown-block .selected-item-text { color: ${link} !important; }
.page-link, .disabled > .page-link { background: ${raised} !important; border: 0 !important; color: ${soft} !important; }
.page-item.active .page-link { background: ${blue} !important; color: #fff !important; }
.dropdown-menu { background: ${raised} !important; border: 0 !important; }
`,

  /** Market Report Email — summary card, symbol report cards, disclaimer. */
  report: `
@import url('${BROKER}/826-mr-app_ds.css');
${BASE}
body, body.fill, .report-container { background: ${page} !important; color: ${text} !important; }
div.summary.row, .summary, .symbol-report, .disclaimer, .archive .dropdown-menu, .header {
  background: ${raised} !important; border: 0 !important; border-radius: 10px !important; box-shadow: none !important;
}
.mr-event-table > tbody > tr:nth-of-type(odd), .mr-market-movement-table > tbody > tr:nth-of-type(even) { background: rgba(255, 255, 255, 0.03) !important; }
.summary .heading, .summary .sub-heading, .symbol-report-top-row > .symbol-name, b, strong, optgroup { color: ${text} !important; }
.disclaimer, .disclaimer * { color: ${muted} !important; }
.btn-archive, .btn-tradeNow { ${PILL} }
.archive .dropdown-menu > li > a { background: ${raised} !important; color: ${text} !important; }
.archive .dropdown-menu > li > a:hover { background: ${hover} !important; }
.archive-modal a, .locale-picker .dropdown-menu > li > a, .symbol-report-top-row { color: ${link} !important; }
`,

  /** Market report sign-up — email, language, session checkboxes, subscribe. */
  signup: `
@import url('${BROKER}/826-signup-form-dark-app.css');
${BASE}
body, #main-container-center { background: ${page} !important; color: ${text} !important; }
#subscription-detail-container-email, #subscription-detail-container-locale {
  background: ${raised} !important; border: 0 !important; border-radius: 7px !important; color: ${text} !important;
}
hr { border: 0 !important; border-top: 1px solid ${line} !important; background: transparent !important; }
.btn-primary, .btn-primary:hover, .btn-primary:active, .btn-primary:disabled { ${PILL} }
.btn-primary:disabled { opacity: 0.5; }
.btn-outline-dark { --bs-btn-border-color: ${raised}; --bs-btn-hover-bg: ${hover}; --bs-btn-active-bg: ${blue}; }
.signup-checkbox:checked, .form-check-input:checked { background-color: ${blue} !important; border-color: ${blue} !important; }
.form-check-input { background-color: ${raised}; border-color: #333; }
#footer-disclaimers > label > p > a { color: ${link} !important; }
#market-reports-explanation, #footer-container { color: ${soft} !important; }
`,
};

/** Comments and layout whitespace out — the sheet travels in a URL. */
const minify = (css) =>
  css
    .replace(/\/\*[\s\S]*?\*\//g, '')
    .replace(/\s+/g, ' ')
    .replace(/\s*([{};,>])\s*/g, '$1')
    // After a colon only: a space before one is a descendant combinator
    // (`body :not(i)`), not layout.
    .replace(/:\s+/g, ':')
    .trim();

const base64 = (text) => btoa(String.fromCharCode(...new TextEncoder().encode(text)));

/**
 * A widget's skin as a stylesheet URL, ready for its `css` parameter.
 *
 * Base64 and minified because most of these widgets take their settings in the
 * query string, and Autochartist's servers answer 400 once a request outgrows
 * their header limit — a percent-encoded sheet, encoded twice over (once as
 * a data URL, once as a parameter), reached that on its own. Base64 survives
 * the parameter's one round of decoding as-is.
 */
export const skin = (name) =>
  encodeURIComponent(`data:text/css;base64,${base64(minify(SKINS[name]))}`);

/**
 * Autochartist's chart theme for the chart images it renders server-side (the
 * Trading Opportunity, Volatility and Report charts are PNGs, so CSS can't
 * reach them). It is base64url-encoded binary — a list of ARGB colours, then
 * date/number formats, the font and the logo URL. Live's copy has two navy
 * backgrounds, FF161D26 and FF202A36; here they are swapped for FF161616
 * (--bg-raised, the cards the charts sit on) and FF1F1F1F, byte for byte, so
 * every other setting is untouched.
 */
export const CHART_STYLE =
  'AAv_FhYW______8fHx__FhYW______________________83yXb_i46T______-LjpP_AAAA____________B2v8____________AAAA_yBs__8kSiz______7M8AAAJTS9kIEhIOm1tAAZkL00veXkACiMsIyMwLjAwMDAABUFyaWFs_wAA________B2v8_4uOk_____8BAQH_SHax_2Sxiv__gEAAAAAAAAAAAAAAQCAAAABQaHR0cHM6Ly9icm9rZXJzbG9nb3MuYXV0b2NoYXJ0aXN0LmNvbS9jdXN0b20tY2hhcnRzL2FscGhhLWNhcGl0YWwtY2hhcnQtbG9nby5wbmc9zMzNAA1NaWRkbGUgQ2VudGVyP4AAAAAAAAAAAAAAABBhdXRvY2hhcnRpc3QuY29t_____wAAAAEAAAAAAf__gED__4BA__-AQP8AAAAAAAAAP4AAAD-AAAA_gAAAP4AAAD-AAAA_gAAAP4AAAD-AAAA_gAAAP4AAAD-AAAA_gAAAP4AAAAAAAAA_gAAAAAAAAAAAAAAAAAAKAAAACgAAAAAAAAAAAAAAAA4AAAAS';
